import { Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import BusinessDirectory from "./pages/BusinessDirectory";
import Events from "./pages/Events";
import About from "./pages/About";
import Advisory from "./pages/Advisory";
import ContactUs from "./pages/ContactUs";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import PrivateRoute from "./components/PrivateRoute";
import { AuthProvider } from "./contexts/AuthContext";
import { AdminProvider } from "./context/AdminContext";
import Home from "./pages/Home";
import Login from "./pages/Login";
import PrivateRouteUser from "./components/PrivateRouteUser";
import SubmitBusiness from "./pages/SubmitBusiness";
import Newsletter from "./pages/Newsletter";
import NewsletterArchive from "./pages/NewsletterArchive";
import ArticlePage from "./pages/ArticlePage";
import BlogEditor from "./pages/BlogEditor";
import AdminForms from "./pages/AdminForms";
import ItemManagement from "./pages/admin/ItemManagement";
import ItemFormPage from "./pages/admin/ItemFormPage";
import ItemsPage from "./pages/ItemsPage";
import ResourcesList from "./pages/ResourcesList";
import ResourcePage from "./pages/ResourcePage";
import EventDetail from "./pages/EventDetail";
import BizConnectShowcase from "./pages/BizConnectShowcase";

// This component wraps the main app content with Navbar and Footer
const MainLayout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow bg-white">
        <div className="h-full mx-auto">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

// This component handles the auth check and renders the appropriate content
const AuthCheck = ({ children }) => {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return children;
};
console.log(process.env.NODE_ENV, process.env.REACT_APP_API_URL);
function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <AuthCheck>
          <Routes>
            {/* Public routes without Navbar/Footer */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/login" element={<Login />} />

            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/blog-editor"
              element={
                <PrivateRoute>
                  <BlogEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/blog-editor/:id"
              element={
                <PrivateRoute>
                  <BlogEditor />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/forms"
              element={
                <PrivateRoute>
                  <AdminForms />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/items"
              element={
                <PrivateRoute>
                  <ItemManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/items/new"
              element={
                <PrivateRoute>
                  <ItemFormPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/items/:id"
              element={
                <PrivateRoute>
                  <ItemFormPage />
                </PrivateRoute>
              }
            />

            {/* Public routes with Navbar/Footer */}
            <Route
              path="/"
              element={
                <MainLayout>
                  <Home />
                </MainLayout>
              }
            />
            <Route
              path="/about"
              element={
                <MainLayout>
                  <About />
                </MainLayout>
              }
            />
            <Route
              path="/events"
              element={
                <MainLayout>
                  <Events />
                </MainLayout>
              }
            />
            {/* Bespoke landing page for Biz Connect Chicago.
                Lives at the top level, not under /events/, so the public URL
                is tr.chicagojamaat.org/bizconnectchicago.
                Keep in sync with OG_OVERRIDES in api/og/index.js — and note
                that a top-level path needs its own route in
                public/staticwebapp.config.json to reach the OG function,
                since only /events/* is intercepted by default. */}
            <Route
              path="/bizconnectchicago"
              element={
                <MainLayout>
                  <BizConnectShowcase />
                </MainLayout>
              }
            />
            {/* The old URL was shared publicly and is still the slug on the
                event record in the DB, which is what the Events list links to.
                Without this it would fall through to /events/:slug and render
                the generic detail page instead of the landing page.
                staticwebapp.config.json also 301s it for scrapers and direct
                hits; this covers client-side navigation.

                That 301 rule MUST stay above the "/events/*" rewrite in
                staticwebapp.config.json. Azure evaluates routes in order and
                first match wins, so if /events/* comes first the old URL is
                served by the OG function and never redirects. The rule cannot
                carry its own note — route entries are schema-restricted to
                route/rewrite/redirect/statusCode/methods/headers/allowedRoles,
                so this is the comment for it. */}
            <Route
              path="/events/biz-connect-showcase-2026"
              element={<Navigate to="/bizconnectchicago" replace />}
            />
            <Route
              path="/events/:slug"
              element={
                <MainLayout>
                  <EventDetail />
                </MainLayout>
              }
            />
            <Route
              path="/advisory"
              element={
                <MainLayout>
                  <Advisory />
                </MainLayout>
              }
            />
            {/* Redirect old newsletter URLs to new resources */}
            <Route
              path="/newsletter"
              element={<Navigate to="/resources/newsletters" replace />}
            />
            <Route
              path="/newsletter/:newsletterId"
              element={
                <MainLayout>
                  <Newsletter />
                </MainLayout>
              }
            />
            <Route
              path="/article/:id"
              element={
                <MainLayout>
                  <ArticlePage />
                </MainLayout>
              }
            />
            {/* Resources routes */}
            <Route
              path="/resources"
              element={
                <MainLayout>
                  <ResourcesList />
                </MainLayout>
              }
            />
            <Route
              path="/resources/:type"
              element={
                <MainLayout>
                  <ResourcesList />
                </MainLayout>
              }
            />
            <Route
              path="/resource/:slug"
              element={
                <MainLayout>
                  <ResourcePage />
                </MainLayout>
              }
            />

            <Route
              path="/contact"
              element={
                <MainLayout>
                  <ContactUs />
                </MainLayout>
              }
            />
            <Route
              path="/business-directory"
              element={
                <MainLayout>
                  <PrivateRouteUser>
                    <BusinessDirectory />
                  </PrivateRouteUser>
                </MainLayout>
              }
            />
            <Route
              path="/submit-business"
              element={
                <MainLayout>
                  <PrivateRouteUser>
                    <SubmitBusiness />
                  </PrivateRouteUser>
                </MainLayout>
              }
            />
            <Route
              path="/items"
              element={
                <MainLayout>
                  <ItemsPage />
                </MainLayout>
              }
            />
          </Routes>
        </AuthCheck>
      </AdminProvider>
    </AuthProvider>
  );
}

export default App;
